import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { resetPasswordSchema, type ResetPasswordInput } from '@rhpc/shared';
import { AuthBrand } from '@/components/BrandMark';
import { Button, Input, ErrorState } from '@rhpc/ui';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const [serverError, setServerError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token: token ?? '' },
    mode: 'onBlur',
  });

  if (!token || !email) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-8">
        <div className="w-full max-w-md">
          <AuthBrand />
          <ErrorState
            title="Invalid reset link"
            happened="This reset link is missing a token or email, or it has expired."
            doing="We cannot reset your password with this URL."
            youCan="Request a new reset link and try again."
            actions={
              <Link to="/forgot-password" className="text-sm font-bold text-primary-text hover:underline">
                Request a new reset link
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const onSubmit = async (data: ResetPasswordInput) => {
    setServerError(null);
    const response = await fetch('/api/v1/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: data.token, password: data.password, confirmPassword: data.confirmPassword }),
    });

    const result = await response.json();

    if (!response.ok) {
      if (result.error?.code === 'INVALID_TOKEN') {
        setServerError('This reset link has expired. Please request a new one.');
      } else {
        setServerError(result.error?.message ?? 'Failed to reset password. Please try again.');
      }
      return;
    }

    navigate('/login', { state: { message: 'Password reset successful. Please sign in.' } });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-8">
      <div className="w-full max-w-[392px]">
        <AuthBrand title="Set New Password" subtitle={email ?? undefined} subtitleClassName="font-mono" />

        <div className="rounded-lg border border-border bg-surface p-8 shadow-sm space-y-4">
          {serverError && (
            <div className="rounded-md border-l-[3px] border-l-danger bg-danger-soft p-3" role="alert">
              <p className="text-sm text-danger">{serverError}</p>
              {serverError.includes('expired') && (
                <Link to="/forgot-password" className="mt-1 block text-sm font-bold text-primary-text hover:underline">
                  Request a new link
                </Link>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <input type="hidden" {...register('token')} value={token} />
            <Input id="password" label="New Password" type="password" placeholder="Min. 8 characters" required error={errors.password?.message} autoComplete="new-password" {...register('password')} />
            <Input id="confirmPassword" label="Confirm Password" type="password" placeholder="Re-enter new password" required error={errors.confirmPassword?.message} autoComplete="new-password" {...register('confirmPassword')} />
            <Button type="submit" loading={isSubmitting} className="w-full">
              Reset Password
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
